import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { LogOut, MapPin } from "lucide-react";
import { useLogout, useSession } from "@/hooks/use-auth";
import { useLocations } from "@/hooks/use-locations";
import { useAuthStore } from "@/store/auth-store";
import { Button } from "@/components/ui/button";
import { FullPageSpinner, Spinner } from "@/components/ui/spinner";

export const Route = createFileRoute("/select-location")({
  // No synchronous ``beforeLoad`` redirect: ``isAuthenticated`` comes from
  // sessionStorage, so a fresh tab reads ``false`` even when the cookie is
  // valid. The auth + locationId checks live in the component below, after
  // ``useSession`` has had a chance to revalidate.
  component: SelectLocationPage,
});

function SelectLocationPage() {
  const navigate = useNavigate();
  const session = useSession();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);

  useEffect(() => {
    if (session.isError) navigate({ to: "/login" });
  }, [session.isError, navigate]);

  // The "Cambiar sede" action clears ``locationId`` before navigating here,
  // so the page only renders when there's actually a choice to make. Picking
  // a sede flips ``locationId`` and this effect handles the redirect — the
  // ``pick`` handler doesn't need its own ``navigate`` call.
  useEffect(() => {
    if (isAuthenticated && locationId) navigate({ to: "/" });
  }, [isAuthenticated, locationId, navigate]);

  const { data: locations, isLoading } = useLocations();
  const setLocation = useAuthStore((s) => s.setLocation);
  const logout = useLogout();

  function pick(id: string) {
    setLocation(id);
  }

  function handleLogout() {
    logout.mutate(undefined, {
      onSettled: () => navigate({ to: "/login" }),
    });
  }

  if (session.isLoading || !isAuthenticated) return <FullPageSpinner />;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-brand-piedra px-6 py-10 text-text-primary">
      <div className="w-full max-w-md rounded-lg border border-border bg-paper p-8 shadow-lg">
        <h1 className="mb-2 font-serif text-3xl italic tracking-tight">Elige una sede</h1>
        <p className="mb-8 text-sm text-text-secondary">
          Tu organización tiene varias sedes. Selecciona con cuál vas a trabajar.
        </p>

        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}

        {!isLoading && locations && locations.length === 0 && (
          <p className="text-sm text-text-secondary">
            Tu organización no tiene sedes configuradas.
          </p>
        )}

        {!isLoading && locations && locations.length > 0 && (
          <ul className="flex flex-col gap-2">
            {locations.map((loc) => (
              <li key={loc.id}>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-auto w-full justify-start gap-3 py-3 text-left"
                  onClick={() => pick(loc.id)}
                >
                  <MapPin className="h-4 w-4 shrink-0 text-text-secondary" />
                  <span className="truncate">{loc.name}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={handleLogout}
          disabled={logout.isPending}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-text-secondary transition-colors hover:text-text-primary disabled:opacity-60"
        >
          <LogOut className="h-3.5 w-3.5" />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
